import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { Icons } from "./bookingConstants";

/* ─── Styles ──────────────────────────────────────────────────────────── */

const S = {
  wrap: { position: "relative", width: "100%" },
  trigger: {
    display: "flex", alignItems: "center", gap: 8, width: "100%",
    padding: "9px 12px", border: "1.5px solid #e5e7eb", borderRadius: 10,
    background: "#fff", fontSize: 13, fontWeight: 500, color: "#111827",
    cursor: "pointer", transition: "border-color .15s", minHeight: 40,
    boxSizing: "border-box",
  },
  triggerOpen: { borderColor: "#6366f1", boxShadow: "0 0 0 3px rgba(99,102,241,.12)" },
  triggerPlaceholder: { color: "#9ca3af", fontWeight: 400 },
  triggerValue: { flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  chevron: { flexShrink: 0, transition: "transform .2s", color: "#9ca3af" },
  chevronOpen: { transform: "rotate(180deg)" },
  dropdown: {
    position: "fixed", background: "#fff", border: "1.5px solid #e5e7eb",
    borderRadius: 12, boxShadow: "0 12px 32px rgba(0,0,0,.12)", zIndex: 9999,
    maxHeight: 320, display: "flex", flexDirection: "column", overflow: "hidden",
    animation: "sldFadeIn .15s ease",
  },
  searchWrap: {
    display: "flex", alignItems: "center", gap: 6, padding: "8px 12px",
    borderBottom: "1px solid #f1f1f1", background: "#fafbfc",
  },
  searchIcon: { color: "#9ca3af", flexShrink: 0 },
  searchInput: {
    flex: 1, border: "none", outline: "none", background: "transparent",
    fontSize: 13, color: "#111827", padding: 0, fontFamily: "inherit",
  },
  list: { overflowY: "auto", flex: 1 },
  groupLabel: {
    padding: "8px 14px 4px", fontSize: 10, fontWeight: 700,
    textTransform: "uppercase", letterSpacing: ".06em", color: "#9ca3af",
    background: "#fafbfc", position: "sticky", top: 0, zIndex: 1,
  },
  item: {
    display: "flex", alignItems: "center", gap: 8, padding: "7px 14px",
    cursor: "pointer", transition: "background .1s", width: "100%",
    border: "none", background: "transparent", textAlign: "left",
    fontSize: 13, fontFamily: "inherit", color: "#111827",
  },
  itemHover: { background: "#f0f0ff" },
  itemSelected: { background: "#eef2ff", fontWeight: 600 },
  empty: { padding: "20px 14px", textAlign: "center", color: "#9ca3af", fontSize: 13 },
  footer: {
    padding: "6px 14px", borderTop: "1px solid #f1f1f1", background: "#fafbfc",
    fontSize: 11, color: "#9ca3af", textAlign: "center",
  },
  clearBtn: {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    width: 18, height: 18, borderRadius: 5, border: "none", background: "#e5e7eb",
    cursor: "pointer", padding: 0, flexShrink: 0, color: "#6b7280",
  },
  addBtn: {
    display: "flex", alignItems: "center", gap: 8, padding: "9px 14px",
    cursor: "pointer", width: "100%", border: "none", borderTop: "1px solid #f1f1f1",
    background: "#eff6ff", textAlign: "left", fontSize: 13, fontFamily: "inherit",
    color: "#1d4ed8", fontWeight: 600,
  },
  addBtnQuote: { color: "#111827", fontWeight: 700 },
  addBtnHint: {
    marginLeft: "auto", flexShrink: 0, fontSize: 10, fontWeight: 700,
    textTransform: "uppercase", letterSpacing: ".05em", color: "#2563eb",
    background: "#dbeafe", borderRadius: 5, padding: "2px 6px",
  },
};

/* Small plus icon for the "add custom" affordance */
const PlusGlyph = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d="M5 12h14" /><path d="M12 5v14" />
  </svg>
);

/* inject keyframes once */
if (typeof document !== "undefined" && !document.getElementById("sld-anim")) {
  const el = document.createElement("style");
  el.id = "sld-anim";
  el.textContent = `@keyframes sldFadeIn{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:translateY(0)}}`;
  document.head.appendChild(el);
}

/**
 * Generic searchable grouped dropdown.
 *
 * Props:
 *   items       — array of { value, group }
 *   value       — current selected value string
 *   onChange    — (value) => void
 *   placeholder — placeholder text
 *   searchPlaceholder — search input placeholder
 *   allowCustom — when true, lets the user add the typed value as a new option
 *                 if it doesn't already exist in the list (create-on-the-fly)
 */
const SearchableListDropdown = ({
  items,
  value,
  onChange,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  allowCustom = false,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [hoverIdx, setHoverIdx] = useState(-1);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);
  const dropdownRef = useRef(null);

  /* ── Filtered + grouped ──────────────────────────────── */
  const { filtered, flatItems } = useMemo(() => {
    const q = search.toLowerCase().trim();
    const matched = q
      ? items.filter((it) => it.value.toLowerCase().includes(q))
      : items;
    const groups = [...new Set(matched.map((it) => it.group))];
    const grouped = groups.map((g) => ({ group: g, items: matched.filter((it) => it.group === g) }));
    const flat = [];
    grouped.forEach((g) => g.items.forEach((it) => flat.push(it)));
    return { filtered: grouped, flatItems: flat };
  }, [items, search]);

  /* ── Custom (create-on-the-fly) option ───────────────── */
  // Show "Add «typed value»" only when allowCustom is on, the user typed
  // something, and that exact value isn't already in the list.
  const trimmed = search.trim();
  const canAddCustom =
    allowCustom &&
    trimmed.length > 0 &&
    !items.some((it) => it.value.toLowerCase() === trimmed.toLowerCase());

  /* ── Position ────────────────────────────────────────── */
  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 4, left: r.left, width: r.width });
  }, []);

  /* ── Click outside ──────────────────────────────────── */
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (
        wrapRef.current && !wrapRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) { setOpen(false); setSearch(""); }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  /* ── Reposition on scroll / resize ──────────────────── */
  useEffect(() => {
    if (!open) return;
    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open, updatePosition]);

  /* ── Focus search ───────────────────────────────────── */
  useEffect(() => {
    if (open && searchRef.current) requestAnimationFrame(() => searchRef.current?.focus());
  }, [open]);

  /* ── Keyboard ───────────────────────────────────────── */
  const handleKeyDown = (e) => {
    if (!open) {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") { e.preventDefault(); setOpen(true); }
      return;
    }
    if (e.key === "Escape") { setOpen(false); setSearch(""); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setHoverIdx((p) => Math.min(p + 1, flatItems.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHoverIdx((p) => Math.max(p - 1, 0)); }
    else if (e.key === "Enter") {
      if (hoverIdx >= 0 && hoverIdx < flatItems.length) {
        e.preventDefault(); onChange(flatItems[hoverIdx].value); setOpen(false); setSearch("");
      } else if (canAddCustom) {
        e.preventDefault(); onChange(trimmed); setOpen(false); setSearch("");
      }
    }
  };

  /* ── Scroll hovered into view ───────────────────────── */
  useEffect(() => {
    if (!open || hoverIdx < 0 || !listRef.current) return;
    const els = listRef.current.querySelectorAll("[data-sld-item]");
    if (els[hoverIdx]) els[hoverIdx].scrollIntoView({ block: "nearest" });
  }, [hoverIdx, open]);

  const handleSelect = (v) => { onChange(v); setOpen(false); setSearch(""); };

  /* ── Portal dropdown ────────────────────────────────── */
  const dropdownEl = open
    ? createPortal(
        <div ref={dropdownRef} style={{ ...S.dropdown, top: pos.top, left: pos.left, width: pos.width }} onKeyDown={handleKeyDown}>
          <div style={S.searchWrap}>
            <span style={S.searchIcon}>{Icons.search}</span>
            <input ref={searchRef} style={S.searchInput} placeholder={searchPlaceholder} value={search}
              onChange={(e) => { setSearch(e.target.value); setHoverIdx(-1); }} />
            {search && <button style={S.clearBtn} type="button" onClick={() => setSearch("")}>{Icons.x}</button>}
          </div>
          <div style={S.list} ref={listRef}>
            {flatItems.length === 0 && !canAddCustom && (
              <div style={S.empty}>No results for "{search}"</div>
            )}
            {filtered.map((grp) => (
              <div key={grp.group}>
                <div style={S.groupLabel}>{grp.group}</div>
                {grp.items.map((it) => {
                  const idx = flatItems.indexOf(it);
                  return (
                    <button key={it.value} type="button" data-sld-item style={{
                      ...S.item,
                      ...(idx === hoverIdx ? S.itemHover : {}),
                      ...(it.value === value ? S.itemSelected : {}),
                    }} onMouseEnter={() => setHoverIdx(idx)} onClick={() => handleSelect(it.value)}>
                      {it.value}
                    </button>
                  );
                })}
              </div>
            ))}
            {canAddCustom && (
              <button type="button" style={S.addBtn}
                onMouseEnter={() => setHoverIdx(-1)}
                onClick={() => handleSelect(trimmed)}>
                <PlusGlyph />
                <span>Add <span style={S.addBtnQuote}>“{trimmed}”</span></span>
                <span style={S.addBtnHint}>new</span>
              </button>
            )}
          </div>
          <div style={S.footer}>
            {flatItems.length} option{flatItems.length !== 1 ? "s" : ""}
            {search ? ` matching "${search}"` : " available"}
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <div ref={wrapRef} style={S.wrap} onKeyDown={handleKeyDown}>
      <button ref={triggerRef} type="button" style={{ ...S.trigger, ...(open ? S.triggerOpen : {}) }}
        onClick={() => { setOpen(!open); setHoverIdx(-1); }}>
        <span style={{ ...S.triggerValue, ...(!value ? S.triggerPlaceholder : {}) }}>
          {value || placeholder}
        </span>
        <span style={{ ...S.chevron, ...(open ? S.chevronOpen : {}) }}>{Icons.chevronDown}</span>
      </button>
      {dropdownEl}
    </div>
  );
};

export default SearchableListDropdown;
